import { useState } from 'react';
import type { CalcRun, CalcStep } from '@ave/shared';
import { dateTime, money } from '../lib/format';
import { Badge, Card, EmptyState, SectionHeading } from './ui';
import { Icon } from './icons';

const FUNCTION_LABELS: Record<string, string> = {
  maintainable_earnings: 'Maintainable earnings',
  capitalised_earnings: 'Capitalised earnings',
  dcf: 'Discounted cash flow',
  nav_assembly: 'Net asset value assembly',
  latent_tax: 'Latent tax',
  discounts: 'Discounts',
  enterprise_to_equity: 'Enterprise to equity bridge',
  holding_value: 'Holding value',
  holding_sensitivity: 'Sensitivity analysis',
  emi_share_values: 'EMI share values (UMV and AMV)',
};

/**
 * The calculation log.
 *
 * This is the working paper. Every figure in the report points into it,
 * and on an HMRC negotiation it is what gets sent. So it shows the
 * arithmetic as arithmetic — "1,850,000.00 − 640,000.00" — rather than a
 * result the reader has to take on trust.
 */
export function CalculationLog({ runs }: { runs: (CalcRun & { steps: CalcStep[] })[] }) {
  const [open, setOpen] = useState<string | null>(runs[runs.length - 1]?.id ?? null);

  if (runs.length === 0) {
    return (
      <Card>
        <SectionHeading title="Calculation log" />
        <EmptyState
          title="Nothing calculated yet"
          detail="The engine writes a run here for every calculation, with each step of its working. Reports bind their figures to these steps."
        />
      </Card>
    );
  }

  return (
    <Card>
      <SectionHeading
        title="Calculation log"
        subtitle={`${runs.length} run${runs.length === 1 ? '' : 's'} · deterministic, replayable, and the source of every figure in the report`}
      />
      <ul className="divide-y divide-slate-100">
        {runs.map((run) => {
          const isOpen = open === run.id;
          const headline = pickHeadline(run);
          return (
            <li key={run.id} data-item-id={run.id} className="scroll-mt-24 rounded-lg">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : run.id)}
                className="flex w-full items-center gap-3 px-6 py-3.5 text-left transition-colors hover:bg-slate-50"
              >
                <Icon
                  name="chevronRight"
                  className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  strokeWidth={2}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-slate-900">
                    {FUNCTION_LABELS[run.functionName] ?? run.functionName}
                  </span>
                  <span className="block text-xs text-slate-400">
                    {run.steps.length} steps · engine {run.engineVersion} ·{' '}
                    {dateTime(run.createdAt)}
                  </span>
                </span>
                {run.status === 'error' ? (
                  <Badge tone="rose">Error</Badge>
                ) : headline ? (
                  <span className="tnum shrink-0 text-sm font-semibold text-slate-900">
                    {headline}
                  </span>
                ) : null}
              </button>

              {isOpen ? (
                <div className="animate-fade-up bg-slate-50/60 px-6 pb-5 pt-1">
                  {run.status === 'error' ? (
                    <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                      {run.error}
                    </p>
                  ) : (
                    <>
                      <ol className="space-y-0">
                        {run.steps.map((step) => (
                          <li
                            key={step.id}
                            className="grid grid-cols-[1.6rem_1fr_auto] items-start gap-x-3 border-b border-slate-100 py-2.5 last:border-0"
                          >
                            <span className="tnum pt-0.5 text-xs text-slate-300">{step.seq}</span>
                            <div className="min-w-0">
                              <p className="text-sm text-slate-800">{step.label}</p>
                              {step.expression ? (
                                <p className="tnum mt-0.5 font-mono text-xs text-slate-500">
                                  {step.expression}
                                </p>
                              ) : null}
                              {step.evidenceRefs.length > 0 || step.citationRefs.length > 0 ? (
                                <div className="mt-1.5 flex flex-wrap gap-1">
                                  {step.citationRefs.map((c) => (
                                    <Badge key={c} tone="violet">
                                      <Icon name="book" className="h-3 w-3" strokeWidth={2} />
                                      {c}
                                    </Badge>
                                  ))}
                                  {step.evidenceRefs.map((e) => (
                                    <Badge key={e} tone="sky">
                                      {e.replace(/^(evidence_point|external_valuation):/, '')}
                                    </Badge>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                            <span className="tnum whitespace-nowrap pt-0.5 text-sm font-medium text-slate-900">
                              {step.value === null
                                ? ''
                                : step.currency
                                  ? money(step.value, step.currency)
                                  : step.value}
                            </span>
                          </li>
                        ))}
                      </ol>

                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
                        <span title="Inputs digest — a replay test compares this to prove determinism">
                          inputs {run.inputsDigest.slice(0, 12)}…
                        </span>
                        {Object.entries(run.libraryVersions).map(([k, v]) => (
                          <span key={k}>
                            {k} {v}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function pickHeadline(run: CalcRun): string | null {
  const o = run.outputs as Record<string, string>;
  const key = [
    'holding_value',
    'net_asset_value',
    'equity_value',
    'enterprise_value',
    'deduction',
    'discounted_value',
    'weighted_maintainable_earnings',
  ].find((k) => o?.[k] !== undefined);
  return key ? money(o[key]) : null;
}
